import { Link } from "react-router-dom";
import { Mail, MapPin, ArrowUpRight, Clock, ShieldCheck } from "lucide-react";
import { SITE, NAV_LINKS, LEGAL_LINKS, VENTURES } from "../../constants";
import { WhatsAppIcon } from "../common/WhatsAppButton";
import { LinkedInIcon } from "../blog/LinkedInPostCard";

export default function Footer({ onPartnerClick }) {
  return (
    <footer id="contact" className="px-3 pt-4 pb-12 sm:px-6 sm:pb-14">
      {/* Floating Island Card Container */}
      <div className="relative mx-auto w-full max-w-[1580px] overflow-hidden rounded-[2rem] bg-gradient-to-b from-[#074639] via-[#063B30] to-[#04241d] px-6 py-9 sm:px-10 sm:py-10 md:px-12 md:py-11 text-white shadow-[0_20px_45px_-12px_rgba(6,59,48,0.38)] ring-1 ring-white/15 border-t border-white/20">
        
        {/* Subtle Ambient Glow */}
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#C89B3C]/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 -bottom-24 h-80 w-80 rounded-full bg-[#0B5D4B]/20 blur-3xl" />

        {/* Main 4-Column Grid */}
        <div className="relative grid gap-8 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8 xl:gap-12 items-start">
          
          {/* Column 1: Brand & Mission (Col 4) */}
          <div className="space-y-4 sm:col-span-2 lg:col-span-4 xl:col-span-4">
            <Link
              to="/"
              className="inline-flex items-center gap-3 transition-transform duration-300 hover:scale-[1.01]"
              data-testid="footer-brand-link"
            >
              <div className="flex items-center gap-3.5 sm:gap-4 rounded-2xl bg-white px-5 py-3 sm:px-6 sm:py-3.5 shadow-md ring-1 ring-white/30 transition-transform duration-300 hover:scale-[1.02]">
                <img
                  src="/crescet-favicon.png"
                  alt={SITE.brand}
                  className="h-14 w-14 sm:h-16 sm:w-16 object-contain shrink-0"
                />
                <img
                  src="/crescet-heading.png"
                  alt={SITE.brand}
                  className="h-12 sm:h-14 md:h-16 w-auto object-contain"
                />
              </div>
            </Link>

            <p className="max-w-sm text-sm leading-relaxed text-white/75">
              An integrated ecosystem uniting career guidance, scientific assessments,
              higher-education admissions, and expert tutoring powered by technology.
            </p>

            <div className="flex items-center gap-2 sm:gap-2.5 pt-1.5 flex-nowrap overflow-x-auto sm:overflow-visible pb-1 sm:pb-0">
              <button
                onClick={onPartnerClick}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#C89B3C] px-3.5 py-2 text-xs font-semibold text-[#063B30] shadow-md shadow-[#C89B3C]/20 transition-all duration-300 hover:bg-[#d8a845] hover:shadow-lg hover:shadow-[#C89B3C]/30 hover:scale-[1.03] active:scale-95 cursor-pointer whitespace-nowrap shrink-0"
                data-testid="footer-partner-btn"
              >
                <span>Partner with us</span>
                <ArrowUpRight size={13} className="shrink-0" />
              </button>
              <a
                href={SITE.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center gap-1.5 rounded-full border border-[#25D366]/35 bg-gradient-to-r from-[#25D366]/15 via-[#25D366]/10 to-transparent px-3.5 py-2 text-xs font-semibold text-white shadow-sm backdrop-blur-md transition-all duration-300 hover:border-[#25D366] hover:bg-[#25D366] hover:text-[#063B30] hover:shadow-lg hover:shadow-[#25D366]/30 hover:scale-[1.03] active:scale-95 whitespace-nowrap shrink-0"
                title="Chat with Crescet Edustreams on WhatsApp"
              >
                <WhatsAppIcon className="h-3.5 w-3.5 text-[#25D366] transition-transform duration-300 group-hover:scale-110 group-hover:text-[#063B30] shrink-0" />
                <span>WhatsApp</span>
                <ArrowUpRight size={12} className="text-white/60 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#063B30] shrink-0" />
              </a>
              {SITE.linkedin && (
                <a
                  href={SITE.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-1.5 rounded-full border border-[#0A66C2]/45 bg-gradient-to-r from-[#0A66C2]/20 via-[#0A66C2]/10 to-transparent px-3.5 py-2 text-xs font-semibold text-white shadow-sm backdrop-blur-md transition-all duration-300 hover:border-[#0A66C2] hover:bg-[#0A66C2] hover:text-white hover:shadow-lg hover:shadow-[#0A66C2]/30 hover:scale-[1.03] active:scale-95 whitespace-nowrap shrink-0"
                  title="Crescet Edustreams on LinkedIn"
                >
                  <LinkedInIcon className="h-3.5 w-3.5 text-[#38bdf8] transition-transform duration-300 group-hover:scale-110 group-hover:text-white shrink-0" fill="currentColor" />
                  <span>LinkedIn</span>
                  <ArrowUpRight size={12} className="text-white/60 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white shrink-0" />
                </a>
              )}
            </div>
          </div>

          {/* Column 2: Connected Ventures (Col 3) */}
          <div className="lg:col-span-3 xl:col-span-3">
            <h4 className="mb-3.5 text-xs font-bold uppercase tracking-[0.2em] text-[#C89B3C]">
              Connected Ventures
            </h4>
            <div className="flex flex-col gap-2">
              {VENTURES.map((v) => (
                <a
                  key={v.name}
                  href={v.url !== "#" ? v.url : undefined}
                  target={v.url !== "#" ? "_blank" : undefined}
                  rel={v.url !== "#" ? "noopener noreferrer" : undefined}
                  className="group flex flex-col rounded-xl px-2.5 py-1.5 -mx-2.5 transition-colors duration-200 hover:bg-white/5"
                >
                  <div className="flex items-center justify-between font-medium text-white">
                    <span className="text-sm transition-colors group-hover:text-[#C89B3C]">{v.name}</span>
                    <ArrowUpRight size={14} className="text-white/35 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#C89B3C]" />
                  </div>
                  <span className="text-xs text-white/55">{v.label}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Column 3: Navigation / Explore (Col 2) */}
          <div className="lg:col-span-2 xl:col-span-2">
            <h4 className="mb-3.5 text-xs font-bold uppercase tracking-[0.2em] text-[#C89B3C]">
              Quick Links
            </h4>
            <div className="flex flex-col gap-2 text-sm text-white/75">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="w-fit transition-all duration-200 hover:text-white hover:translate-x-1"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Column 4: Contact & Info (Col 3) */}
          <div className="lg:col-span-3 xl:col-span-3">
            <h4 className="mb-3.5 text-xs font-bold uppercase tracking-[0.2em] text-[#C89B3C]">
              Contact & Info
            </h4>
            <div className="space-y-3 text-sm text-white/75">
              <a
                href={`mailto:${SITE.email}`}
                className="group flex items-center gap-2.5 text-white/85 transition-colors hover:text-white"
              >
                <Mail size={15} className="shrink-0 text-[#C89B3C]" />
                <span className="truncate group-hover:underline">{SITE.email}</span>
              </a>

              <div className="flex items-start gap-2.5 text-white/85">
                <MapPin size={15} className="mt-0.5 shrink-0 text-[#C89B3C]" />
                <span className="leading-relaxed">{SITE.address}</span>
              </div>

              <div className="flex items-center gap-2.5 text-white/85">
                <Clock size={15} className="shrink-0 text-[#C89B3C]" />
                <span className="text-xs text-white/70">Mon – Sat: 9:00 AM – 7:00 PM IST</span>
              </div>

            </div>
          </div>
        </div>

        {/* Sub-footer Divider & Copyright */}
        <div className="relative mt-8 sm:mt-10 flex flex-col justify-between gap-4 border-t border-white/10 pt-6 text-xs text-white/50 md:flex-row md:items-center">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-white/60">
            <span>&copy; {new Date().getFullYear()} Crescet EduStreams Consulting OPC Private Limited. All rights reserved.</span>
            <span className="hidden sm:inline text-white/20">•</span>
            <span className="inline-flex items-center gap-1.5 text-white/75 font-medium">
              <ShieldCheck size={14} className="text-[#C89B3C]" />
              ISO 9001:2015 Certified Organization
            </span>
          </div>
          
          <div className="flex flex-wrap items-center gap-4 text-white/60">
            {LEGAL_LINKS.map((l, i) => (
              <span key={l.to} className="flex items-center gap-4">
                <Link
                  to={l.to}
                  className="transition-colors hover:text-white"
                >
                  {l.label}
                </Link>
                {i < LEGAL_LINKS.length - 1 && (
                  <span className="text-white/20 select-none">•</span>
                )}
              </span>
            ))}
          </div>
        </div>

      </div>
    </footer>
  );
}
